import React, { useState, useEffect } from "react";
import { useAtom } from "jotai";
import { motion } from "framer-motion";
import { DragDropContext, Draggable } from "@hello-pangea/dnd";
import { StrictModeDroppable as Droppable } from "../../../../../GlobalComponents/StrictModeDroppable";
import { themeAtom } from "../../../../../-Atoms";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { v4 as uuidv4 } from "uuid";
import { db, doc, updateDoc } from "../../../../../firebaseConfig";

import CateringItemV2 from "./CateringItemV2";

const CateringV2 = ({ catering, setCatering, jobData, updateFS }) => {
  const [theme] = useAtom(themeAtom);
  const [cateringTotal, setCateringTotal] = useState(0.0);
  const [showRemoveCateringItem, setShowRemoveCateringItem] = useState(false);

  useEffect(() => {
    let total = 0;
    catering?.forEach((ci) => {
      if (ci.amount) {
        total = parseFloat(total) + parseFloat(ci.amount);
      }
    });
    setCateringTotal(total.toFixed(2));
  }, [catering]);

  const addCateringItem = () => {
    const newitemObj = {
      id: uuidv4(),
      amount: "",
      store: "",
    };
    const newCatering = [...(catering || []), newitemObj];
    setCatering(newCatering);
    updateDoc(doc(db, "JobData", jobData.id), { catering: newCatering }).catch(
      (e) => console.log(e)
    );
  };

  const toggleShowRemoveCateringItem = () => {
    setShowRemoveCateringItem(true);
    setTimeout(() => {
      setShowRemoveCateringItem(false);
    }, 6000);
  };

  const handleDragEnd = (result) => {
    if (!result.destination || !catering) return;

    const items = Array.from(catering);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setCatering(items);
    updateDoc(doc(db, "JobData", jobData.id), { catering: items }).catch((e) =>
      console.log(e)
    );
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.1, 0.25, 1.0],
      },
    },
  };

  return (
    <motion.div variants={itemVariants}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          borderRadius: "10px",
          backgroundColor:
            theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow:
            theme === "dark"
              ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
              : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
          padding: 2,
          marginBottom: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: "1.1rem",
              fontWeight: "bold",
              color:
                theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)",
            }}
          >
            Catering - ${cateringTotal}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              onClick={toggleShowRemoveCateringItem}
              sx={{
                color:
                  theme === "dark"
                    ? "rgba(255,100,100,0.8)"
                    : "rgba(200,50,50,0.8)",
              }}
            >
              <RemoveIcon />
            </IconButton>
            <IconButton
              onClick={addCateringItem}
              sx={{
                color:
                  theme === "dark"
                    ? "rgba(100,255,100,0.8)"
                    : "rgba(50,150,50,0.8)",
              }}
            >
              <AddIcon />
            </IconButton>
          </Box>
        </Box>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="catering-list">
            {(provided, snapshot) => (
              <Box
                {...provided.droppableProps}
                ref={provided.innerRef}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  backgroundColor: snapshot.isDraggingOver
                    ? theme === "dark"
                      ? "rgba(100,150,255,0.15)"
                      : "rgba(100,150,255,0.20)"
                    : "transparent",
                  borderRadius: "8px",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  minHeight: catering?.length === 0 ? "60px" : "auto",
                  border: snapshot.isDraggingOver
                    ? `2px dashed ${
                        theme === "dark"
                          ? "rgba(100,150,255,0.3)"
                          : "rgba(100,150,255,0.4)"
                      }`
                    : "2px dashed transparent",
                  padding: snapshot.isDraggingOver ? 1 : 0,
                }}
              >
                {catering &&
                  catering.map((ca, index) => (
                    <Draggable key={ca.id} draggableId={ca.id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={{
                            ...provided.draggableProps.style,
                            zIndex: snapshot.isDragging ? 1000 : 1,
                            opacity: snapshot.isDragging ? 0.9 : 1,
                            boxShadow: snapshot.isDragging
                              ? `0 8px 32px ${
                                  theme === "dark"
                                    ? "rgba(0,0,0,0.5)"
                                    : "rgba(0,0,0,0.2)"
                                }`
                              : "none",
                          }}
                        >
                          <CateringItemV2
                            item={ca}
                            items={catering}
                            setItems={setCatering}
                            index={index}
                            showRemoveCateringItem={showRemoveCateringItem}
                            jobData={jobData}
                            isDragging={snapshot.isDragging}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
    </motion.div>
  );
};

export default CateringV2;
